import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import toast from 'react-hot-toast';
import { Transition } from '@headlessui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { $convertToMarkdownString } from '@lexical/markdown';
import { MARKDOWN_TRANSFORMERS } from '../../../components/Editor/plugins/MarkdownTransformers';
import { LexicalEditor } from 'lexical';
import {
  ClockIcon,
  DownloadIcon,
  LockClosedIcon,
  LockOpenIcon,
  MailIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { Transition as HistoryTransition } from 'history';

import {
  BasicProviderUserDataFragment,
  ProgramNoteDataFragment,
  ProgramNoteShareStatus,
  UpdateProgramNoteInput,
  useDeleteProgramNoteMutation,
  useProgramNoteSharesLazyQuery,
  useRecordProgramNotePdfDownloadMutation,
  useUpdateProgramNoteMutation,
} from '../../../../generated/graphql';

import Spinner from '../../../svgs/Spinner';
import { defaultTransitionProps } from '../../../lib/animation';
import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';

import ToastAlert from '../../../components/ToastAlert';
import padlock from '../../../../assets/svgs/padlock.svg';

import { usePatientProfileContext } from '..';
import Editor from '../../../components/Editor';
import { useForm } from 'react-hook-form';
import InputGroup from '../../../components/InputGroup';
import Trash from '../../../svgs/Trash';
import debounce from 'lodash.debounce';
import useBlocker from '../../../hooks/useBlocker';
import Alert from '../../../components/Alert';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import Avatar from '../../../components/Avatar';
import Pin from '../../../svgs/Pin';
import PinFilled from '../../../svgs/PinFilled';
import { customToast } from '../../../components/ToastAlert/customToast';
import NoteActivityModal from '../../../components/Modals/NoteActivityModal';
import NotifyProviderUsersModal from '../../../components/Modals/NotifyProviderUsersModal';
import { useAuth } from '../../../../contexts/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ProgramNotePDF from '../../../components/ProgramNotePDF';
import { handleProgramNotePdfDownload } from '../../../lib/pdf';

interface NoteFormData {
  title: string;
}
const SAVE_NOTE_DEBOUNCE = 1000;

const NoteDetails = () => {
  const params = useParams();
  const programNoteIdFromRoute = params.programNoteId ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const isNewNote = searchParams.get('new') === 'true';

  const {
    refetchProgramNotes,
    refetchPinnedNotes,
    selectedProgramNote,
    isSearching,
    isUnsavedChangesModalOpen,
    setIsUnsavedChangesModalOpen,
    setNavTransition,
    patient,
  } = usePatientProfileContext();

  const { authedProviderUser } = useAuth();

  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isSaveNotePending, setIsSaveNotePending] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const programNoteId = useMemo(
    () => programNoteIdFromRoute ?? selectedProgramNote?.id ?? null,
    [programNoteIdFromRoute, selectedProgramNote?.id],
  );

  const shouldAutoOpenNotifyModal = useMemo(() => {
    const params = new URLSearchParams(searchParamsString);
    return params.get('notify') === 'true';
  }, [searchParamsString]);

  const clearNotifySearchParam = useCallback(() => {
    const nextParams = new URLSearchParams(searchParamsString);
    if (!nextParams.has('notify')) return;
    nextParams.delete('notify');
    setSearchParams(nextParams, { replace: true });
  }, [searchParamsString, setSearchParams]);

  const blocker = useCallback((navTransition: HistoryTransition) => {
    if (!isUnsavedChangesModalOpen) {
      setNavTransition(navTransition);
      setIsUnsavedChangesModalOpen(true);
    }
  }, []);

  useBlocker(blocker, isSavingNote || isSaveNotePending);

  const editorRef = useRef<LexicalEditor | null>(null);

  useEffect(() => {
    if (shouldAutoOpenNotifyModal && selectedProgramNote?.id) {
      setIsNotifyModalOpen(true);
      clearNotifySearchParam();
    }
  }, [
    shouldAutoOpenNotifyModal,
    selectedProgramNote?.id,
    clearNotifySearchParam,
  ]);

  const [deleteProgramNote, { loading: isDeleteProgramNoteLoading }] =
    useDeleteProgramNoteMutation();

  const [fetchProgramNoteShares, { data: programNoteSharesData }] =
    useProgramNoteSharesLazyQuery({
      fetchPolicy: 'network-only',
    });

  const activeExternalShareCount = useMemo(() => {
    return (
      programNoteSharesData?.programNoteShares?.filter(
        (share) => share.status === ProgramNoteShareStatus.Active,
      ).length ?? 0
    );
  }, [programNoteSharesData?.programNoteShares]);

  const [recordProgramNotePdfDownload] =
    useRecordProgramNotePdfDownloadMutation();

  const [updateProgramNote, { loading: isUpdateProgramNoteLoading }] =
    useUpdateProgramNoteMutation();

  const {
    register,
    setValue,
    formState: { errors: validationErrors },
    setFocus,
  } = useForm<NoteFormData>({
    mode: 'onChange',
    defaultValues: { title: '' },
  });

  useEffect(() => {
    setTimeout(() => {
      setFocus('title');
    }, 50);
  }, [programNoteId]);

  const deleteNote = async () => {
    const noteIdForActions = programNoteId;
    if (!noteIdForActions) return;
    try {
      await deleteProgramNote({
        variables: {
          programNoteId: noteIdForActions,
        },
      });

      navigate('../');
      // Force reload the page to refetch the data and use the parent select note logic
      navigate(0);
    } catch (error) {
      console.error(error);

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to delete the note."
          level="error"
        />
      ));
    }
  };

  const saveNote = async ({
    programNoteId,
    title,
    note,
  }: {
    programNoteId: string;
    title?: string;
    note?: string;
  }) => {
    setIsSavingNote(true);
    setIsSaveNotePending(false);
    try {
      const updateProgramNoteInput: UpdateProgramNoteInput = {
        programNoteId: programNoteId,
        ...(title && { title }),
        ...(note && { note }),
      };

      await updateProgramNote({
        variables: {
          input: updateProgramNoteInput,
        },
      });

      refetchProgramNotes?.();
      refetchPinnedNotes?.();
    } catch (error) {
      console.error(error);

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to update the note."
          level="error"
        />
      ));
    } finally {
      setIsSavingNote(false);
    }
  };

  const debouncedSaveNote = useCallback(
    debounce(saveNote, SAVE_NOTE_DEBOUNCE, {
      leading: false,
      trailing: true,
    }),
    [],
  );

  const status =
    isSavingNote || isSaveNotePending ? (
      <>
        <RefreshIcon className="mr-1 h-4 w-4" />
        Saving...
      </>
    ) : (
      <>
        Last saved {getRelativeTimestamp(selectedProgramNote?.updatedAt)}
        <img src={padlock} alt="private note" className="ml-1 h-4 w-4" />
      </>
    );

  // When a different note is selected
  useEffect(() => {
    if (!selectedProgramNote?.id) return;
    if (selectedProgramNote?.title) {
      setValue('title', selectedProgramNote.title);
    }
  }, [selectedProgramNote?.id]);

  const previousProgramNoteIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previousId = previousProgramNoteIdRef.current;
    const currentId = selectedProgramNote?.id ?? null;

    if (previousId && currentId && previousId !== currentId) {
      setIsNotifyModalOpen(false);
      setIsActivityModalOpen(false);
      clearNotifySearchParam();
    }

    previousProgramNoteIdRef.current = currentId;
  }, [selectedProgramNote?.id, clearNotifySearchParam]);

  const handleLockNote = async (programNote: ProgramNoteDataFragment) => {
    try {
      setIsSavingNote(true);
      setIsSaveNotePending(false);

      await updateProgramNote({
        variables: {
          input: {
            programNoteId: programNote.id,
            isLocked: !programNote.isLocked,
          },
        },
      });

      refetchProgramNotes?.();
      refetchPinnedNotes?.();

      const lockedMessage = `Your note '${programNote.title}' is now locked and read-only.`;
      const unlockedMessage = `Your note '${programNote.title}' is now unlocked and can be edited.`;

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={programNote.isLocked ? unlockedMessage : lockedMessage}
        />
      ));
    } catch (error) {
      console.error(error);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to update the note."
          level="error"
        />
      ));
    } finally {
      setIsSavingNote(false);
    }
  };

  const handlePinNote = async (programNote: ProgramNoteDataFragment) => {
    try {
      setIsSavingNote(true);
      setIsSaveNotePending(false);

      await updateProgramNote({
        variables: {
          input: {
            programNoteId: programNote.id,
            isPinnedNote: !programNote.isPinnedNote,
          },
        },
      });

      refetchProgramNotes?.();
      refetchPinnedNotes?.();

      const pinnedNoteMessage = `Your note '${programNote.title}' is pinned and will now appear at the top.`;
      const unpinnedNoteMessage = `Your note '${programNote.title}' is unpinned.`;

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={
            !programNote.isPinnedNote ? pinnedNoteMessage : unpinnedNoteMessage
          }
        />
      ));
    } catch (error) {
      console.error(error);

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to update the note."
          level="error"
        />
      ));
    } finally {
      setIsSavingNote(false);
    }
  };

  const notifyButtonLabel = 'Share note';
  const programId = params.programId ?? '';

  const handleNotifySuccess = ({
    notifiedUsers,
    sharedContacts,
  }: {
    notifiedUsers: BasicProviderUserDataFragment[];
    sharedContacts: Array<{ name: string; email: string }>;
  }) => {
    const teammateCount = notifiedUsers.length;
    const contactCount = sharedContacts.length;
    const parts: string[] = [];

    if (teammateCount > 0) {
      parts.push(`${teammateCount} teammate${teammateCount === 1 ? '' : 's'}`);
    }
    if (contactCount > 0) {
      parts.push(`${contactCount} contact${contactCount === 1 ? '' : 's'}`);
    }

    if (parts.length === 0) {
      customToast.success('Note shared successfully.');
    } else {
      customToast.success(`Note shared with ${parts.join(' and ')}.`);
    }

    clearNotifySearchParam();
  };

  const handleOpenNotify = useCallback(() => {
    if (!selectedProgramNote?.id || !authedProviderUser?.id) {
      return;
    }

    if (!programNoteIdFromRoute) {
      const nextParams = new URLSearchParams(searchParamsString);
      nextParams.set('notify', 'true');
      navigate(
        {
          pathname: selectedProgramNote.id,
          search: nextParams.toString(),
        },
        { replace: true },
      );
      return;
    }

    setIsNotifyModalOpen(true);
  }, [
    authedProviderUser?.id,
    navigate,
    programNoteIdFromRoute,
    searchParamsString,
    selectedProgramNote?.id,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    if (!selectedProgramNote) return;

    try {
      setIsDownloadingPdf(true);
      const patientName = patient
        ? `${patient.firstName} ${patient.lastName}`
        : undefined;
      const wordmarkLogoUrl =
        patient?.providerWordmarkLogo?.url ||
        authedProviderUser?.wordmarkLogoMedia?.url ||
        null;
      const participantId = patient?.participantId || null;

      const pdfDocument = (
        <ProgramNotePDF
          programNote={selectedProgramNote}
          patientName={patientName}
          wordmarkLogoUrl={wordmarkLogoUrl}
          participantId={participantId}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();

      handleProgramNotePdfDownload(
        blob,
        selectedProgramNote,
        patientName || 'Client',
        () => {
          customToast.success('PDF downloaded successfully');
        },
      );

      try {
        await recordProgramNotePdfDownload({
          variables: { programNoteId: selectedProgramNote.id },
        });
      } catch (auditError) {
        console.error(
          'Failed to record PDF download in activity log:',
          auditError,
        );
      }
    } catch (error) {
      console.error('PDF download error:', error);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to download the note as PDF."
          level="error"
        />
      ));
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [
    selectedProgramNote,
    patient,
    recordProgramNotePdfDownload,
    authedProviderUser,
  ]);

  return (
    <>
      <Transition
        show={!!selectedProgramNote}
        {...defaultTransitionProps}
        className="h-full w-full"
      >
        <div className="flex h-full w-full flex-col pl-5">
          <div
            className={`h-full w-full rounded-t-xl border-l border-r border-t px-8 pt-5 ${
              selectedProgramNote?.isLocked
                ? 'border-l-warning-100 border-l-4 border-neutral-75 bg-neutral-50'
                : 'border-neutral-75'
            }`}
          >
            <div className="relative mb-3 flex w-full items-center justify-center">
              {selectedProgramNote && (
                <div className="absolute left-0 flex flex-row">
                  <Avatar
                    size="small"
                    imageUrl={
                      selectedProgramNote?.createdByProviderUser
                        .profileImageMedia?.url
                    }
                    name={selectedProgramNote?.createdByProviderUser.name}
                  />
                  <div className="ml-2 flex flex-col justify-center">
                    <div className="text-caption font-medium text-neutral-125">
                      {selectedProgramNote?.createdByProviderUser.name}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-row items-center justify-center p-1 text-center text-small-caption text-neutral-125">
                {status}
              </div>
              <div className="absolute right-0">
                <ActionButtonGroup size="small">
                  <ActionButton
                    size="small"
                    aria-label="Activity history"
                    title="Activity history"
                    disabled={!selectedProgramNote?.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsActivityModalOpen(true);
                    }}
                  >
                    <ClockIcon className="h-4 w-4 text-neutral-125" />
                  </ActionButton>
                  <ActionButton
                    size="small"
                    aria-label="Download PDF"
                    title="Download PDF"
                    disabled={!selectedProgramNote?.id || isDownloadingPdf}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadPdf();
                    }}
                  >
                    <DownloadIcon className="h-4 w-4 text-neutral-125" />
                  </ActionButton>
                  <ActionButton
                    size="small"
                    aria-label={notifyButtonLabel}
                    title={notifyButtonLabel}
                    disabled={
                      !selectedProgramNote?.id || !authedProviderUser?.id
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNotify();
                    }}
                  >
                    <MailIcon className="h-4 w-4 text-neutral-125" />
                  </ActionButton>
                  {selectedProgramNote?.canDelete && (
                    <ActionButton
                      size="small"
                      aria-label="Delete note"
                      title="Delete note"
                      disabled={isDeleteProgramNoteLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (programNoteId) {
                          void fetchProgramNoteShares({
                            variables: { programNoteId },
                          });
                        }
                        setIsDeleteNoteModalOpen(true);
                      }}
                    >
                      <Trash className="h-4 w-4 text-neutral-125" />
                    </ActionButton>
                  )}
                  {selectedProgramNote?.canLockUnlock && (
                    <ActionButton
                      size="small"
                      aria-label={
                        selectedProgramNote.isLocked
                          ? 'Unlock note'
                          : 'Lock note'
                      }
                      title={
                        selectedProgramNote.isLocked
                          ? 'Unlock note'
                          : 'Lock note'
                      }
                      disabled={isUpdateProgramNoteLoading}
                      isActive={Boolean(selectedProgramNote?.isLocked)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSaveNotePending(true);
                        handleLockNote(selectedProgramNote);
                      }}
                    >
                      {selectedProgramNote?.isLocked ? (
                        <LockClosedIcon className="h-4 w-4 text-secondary-125" />
                      ) : (
                        <LockOpenIcon className="h-4 w-4 text-neutral-150" />
                      )}
                    </ActionButton>
                  )}
                  <ActionButton
                    size="small"
                    aria-label={
                      !selectedProgramNote?.isPinnedNote
                        ? 'Pin note'
                        : 'Unpin note'
                    }
                    title={
                      !selectedProgramNote?.isPinnedNote
                        ? 'Pin note'
                        : 'Unpin note'
                    }
                    disabled={isUpdateProgramNoteLoading}
                    isActive={Boolean(selectedProgramNote?.isPinnedNote)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSaveNotePending(true);
                      handlePinNote(selectedProgramNote);
                    }}
                  >
                    {selectedProgramNote?.isPinnedNote ? (
                      <PinFilled className="h-4 w-4 text-secondary-125" />
                    ) : (
                      <Pin className="h-4 w-4 text-neutral-150" />
                    )}
                  </ActionButton>
                </ActionButtonGroup>
              </div>
            </div>
            {selectedProgramNote && (
              <div className="mb-3 flex flex-row gap-3 text-small-caption text-neutral-125">
                <div>
                  <span className="font-medium">Created:</span>{' '}
                  {getFullDateTimeStamp(selectedProgramNote.createdAt)}
                </div>
                <div>
                  <span className="font-medium">Last updated:</span>{' '}
                  {getFullDateTimeStamp(selectedProgramNote.updatedAt)}
                </div>
              </div>
            )}
            <div className="mb-6 flex w-full flex-col">
              <InputGroup
                autoFocus={isNewNote}
                required
                labelHidden
                backgroundHidden
                label="Note title"
                placeholder="Untitled note"
                className="rounded-none border-0 border-b-2 px-0 pb-1 font-serif text-big-label text-neutral-150"
                errorMessage={validationErrors.title?.message}
                disabled={selectedProgramNote?.isLocked}
                {...register('title', {
                  required: 'Title is required',
                  onChange: (event) => {
                    if (
                      selectedProgramNote?.id &&
                      !selectedProgramNote?.isLocked
                    ) {
                      debouncedSaveNote({
                        programNoteId: selectedProgramNote.id,
                        title: event.target.value,
                      });
                    }
                  },
                })}
              />
            </div>

            <Editor
              initialContentMarkdown={selectedProgramNote?.note}
              elKey={selectedProgramNote?.id}
              placeholder="Start your note..."
              className="text-neutral-150"
              contentClassName="overflow-y-auto max-h-[calc(100vh-420px)] pr-2"
              innerRef={editorRef}
              readOnly={selectedProgramNote?.isLocked}
              onChange={(editorState) => {
                if (isSearching || selectedProgramNote?.isLocked) return;
                editorState.read(() => {
                  const updatedNote = $convertToMarkdownString(
                    MARKDOWN_TRANSFORMERS,
                    undefined,
                    true,
                  );
                  if (
                    selectedProgramNote?.id &&
                    selectedProgramNote?.note !== updatedNote
                  ) {
                    setIsSaveNotePending(true);
                    debouncedSaveNote({
                      programNoteId: selectedProgramNote.id,
                      note: updatedNote,
                    });
                  }
                });
              }}
              onError={(error: Error) => {
                console.error(error);
              }}
            />
          </div>
        </div>
      </Transition>

      {!selectedProgramNote && <Spinner className="mx-auto mt-8" />}

      {selectedProgramNote?.id && (
        <NoteActivityModal
          isOpen={isActivityModalOpen}
          setClosed={() => setIsActivityModalOpen(false)}
          programNoteId={selectedProgramNote.id}
          noteTitle={selectedProgramNote.title}
        />
      )}

      {selectedProgramNote?.id && authedProviderUser?.id && (
        <NotifyProviderUsersModal
          isOpen={isNotifyModalOpen}
          setClosed={() => {
            setIsNotifyModalOpen(false);
            clearNotifySearchParam();
          }}
          programNoteId={selectedProgramNote.id}
          programId={programId}
          noteTitle={selectedProgramNote.title}
          authedProviderUserId={authedProviderUser.id}
          onSuccess={handleNotifySuccess}
        />
      )}

      <ConfirmDeleteModal
        title="Would you like to delete this note?"
        actionButtonTitle="Delete note"
        isOpen={isDeleteNoteModalOpen}
        fetching={isDeleteProgramNoteLoading}
        setClosed={() => setIsDeleteNoteModalOpen(false)}
        performDelete={deleteNote}
      >
        <div className="flex flex-col gap-3 text-left">
          {activeExternalShareCount > 0 && (
            <Alert level="warning" message="This note has active share links">
              <p className="mt-1">
                This note has been shared externally and still has{' '}
                {activeExternalShareCount === 1
                  ? '1 active link'
                  : `${activeExternalShareCount} active links`}
                . Deleting the note will not revoke those links — recipients can
                still view the shared snapshot until the links expire or are
                revoked.
              </p>
            </Alert>
          )}
          <p className="text-center">You can&apos;t undo this action.</p>
        </div>
      </ConfirmDeleteModal>
    </>
  );
};

export default NoteDetails;
